import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationLabel } from '@/components/medcom/OrganizationName';
import { formatMedcomDateTime } from '@/lib/medcom/parseCareCommunicationBundle';
import {
  priorSegmentsForForward,
  type PriorSegment,
  type PriorSegmentSignature,
} from '@/lib/medcom/priorSegments';
import type { ForwardChoice } from './useForwardChoice';

type ForwardSource = Parameters<typeof priorSegmentsForForward>[0];

/**
 * Earlier letter above the new text. A forward passes `choice` so parts can be ticked off.
 * A bad segment stays out of the form instead of leaving the page blank.
 */
export function ForwardPriorMessage(props: ForwardSource & { choice?: ForwardChoice }) {
  const { choice, ...source } = props;
  return (
    <PreviewBoundary>
      <PriorMessageBody source={source} choice={choice} />
    </PreviewBoundary>
  );
}

function PriorMessageBody({ source, choice }: { source: ForwardSource; choice?: ForwardChoice }) {
  return <PriorMessagePreview segments={priorSegmentsForForward(source)} choice={choice} />;
}

class PreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error(error);
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/** Text, signature, and attachment titles from the letter being forwarded. */
export function PriorMessagePreview({
  segments,
  choice,
}: {
  segments: PriorSegment[];
  choice?: ForwardChoice;
}) {
  const { t } = useTranslation();
  const picking = choice?.selectable ? choice : undefined;
  const texts = segments.filter((segment) => segment.kind === 'text');
  const attachments = segments.filter((segment) => segment.kind === 'attachment');
  if (texts.length === 0 && attachments.length === 0) return null;

  return (
    <div className="space-y-3">
      {picking && (
        <p className="text-sm text-ds-muted">
          {t('communicate.forward_choose_hint')}
        </p>
      )}
      {texts.length > 0 && (
        <div className="rounded-md border border-ds-line bg-ds-sunk p-3">
          <h4 className="text-sm font-semibold text-ds-text">
            {t('communicate.prior_message_title')}
          </h4>
          <div className="mt-3 space-y-3">
            {texts.map((segment) => (
              <article key={segment.key}>
                {picking && (
                  <div className="mb-1">
                    <ChooseBox
                      checked={picking.isChosen(segment.key)}
                      onChange={() => picking.toggle(segment.key)}
                      label={t('communicate.forward_choose_text')}
                    />
                  </div>
                )}
                {segment.timestamp && (
                  <p className="mb-1 text-xs text-ds-muted">
                    {formatMedcomDateTime(segment.timestamp) ?? segment.timestamp}
                  </p>
                )}
                <p className="whitespace-pre-wrap text-sm text-ds-text">
                  {segment.text}
                </p>
                {segment.signature && (
                  <SignatureBlock signature={segment.signature} timestamp={segment.timestamp} />
                )}
              </article>
            ))}
          </div>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="rounded-md border border-ds-line bg-ds-sunk p-3">
          <h4 className="text-sm font-semibold text-ds-text">
            {t('communicate.prior_attachments')}
          </h4>
          <ul className="mt-2 space-y-1">
            {attachments.map((segment) => (
              <li key={segment.key} className="flex items-center gap-2 text-sm text-ds-text">
                {picking && (
                  <ChooseBox
                    checked={picking.isChosen(segment.key)}
                    onChange={() => picking.toggle(segment.key)}
                    label={t('communicate.forward_choose_attachment')}
                  />
                )}
                {segment.title ?? t('communicate.attachment_label')}
              </li>
            ))}
          </ul>
        </div>
      )}
      {picking?.nothingChosen && (
        <p className="text-sm text-ds-red-ink">
          {t('communicate.forward_choose_none')}
        </p>
      )}
    </div>
  );
}

function ChooseBox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
    />
  );
}

function SignatureBlock({
  signature,
  timestamp,
}: {
  signature: PriorSegmentSignature;
  timestamp: string | null;
}) {
  const { t } = useTranslation();
  const at = formatMedcomDateTime(timestamp);
  const rows = [
    { label: t('communicate.prior_name'), value: signature.name },
    { label: t('communicate.prior_role'), value: signature.role },
    { label: t('communicate.prior_phone'), value: signature.phone },
    { label: t('communicate.prior_date'), value: at },
    {
      label: t('communicate.prior_organization'),
      value: signature.organization ? <OrganizationLabel label={signature.organization} /> : null,
    },
  ].filter((row) => row.value);
  if (rows.length === 0) return null;

  return (
    <div className="mt-2 rounded border border-ds-line bg-ds-card p-3">
      <p className="mb-2 text-xs font-semibold text-ds-text">
        {t('communicate.prior_signature')}
      </p>
      <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-ds-muted">{row.label}</dt>
            <dd className="mt-0.5 text-ds-text">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
